import { Button, Input, VStack } from '@chakra-ui/react'
import React, { useEffect, useState } from 'react'

import { Field, Modal } from '@mangovoice/mango-ui'

import type {
  Playlist,
  UpdatePlaylistPayload,
} from '../api/musicOnHoldApi.types'
import { type MusicOnHoldTranslateFn, text } from '../types/i18n.types'

export interface EditPlaylistModalProps {
  open: boolean
  playlist: Pick<Playlist, 'name'>
  onClose: () => void
  onSave: (payload: UpdatePlaylistPayload) => void
  isSaving?: boolean
  t?: MusicOnHoldTranslateFn
}

export const EditPlaylistModal: React.FC<EditPlaylistModalProps> = ({
  open,
  playlist,
  onClose,
  onSave,
  isSaving = false,
  t,
}) => {
  const [name, setName] = useState(playlist.name)

  // Re-seed the field whenever the dialog is (re)opened for a playlist, so a
  // stale local edit from a previous open doesn't leak in.
  useEffect(() => {
    if (open) {
      setName(playlist.name)
    }
  }, [open, playlist.name])

  const isValid = name.trim().length > 0

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (!isValid) return
    // Send url: "" (not null). music-service's UpdatePlaylistRequest.url isn't
    // nullable (a null is rejected by its request validator with a 400), but an
    // empty string is accepted and the handler maps "" -> NULL — so this
    // unblocks plain name edits on list playlists.
    onSave({ name: name.trim(), type: 'list', url: '' })
  }

  return (
    <Modal
      open={open}
      onOpenChange={(details) => !details.open && onClose()}
      title={text(t, 'musicOnHold.editModal.title', 'Edit Playlist')}
      maxWidth="lg"
      footer={
        <>
          <Button borderRadius={'lg'} variant="outline" onClick={onClose}>
            {text(t, 'musicOnHold.editModal.cancel', 'Cancel')}
          </Button>
          <Button
            type="submit"
            form="edit-playlist-form"
            borderRadius={'lg'}
            colorPalette="brand"
            bg="brand.solid"
            color="brand.contrast"
            disabled={!isValid || isSaving}
            loading={isSaving}
          >
            {text(t, 'musicOnHold.editModal.submit', 'Save Changes')}
          </Button>
        </>
      }
    >
      <form id="edit-playlist-form" onSubmit={handleSubmit}>
        <VStack gap={5} align="stretch">
          <Field.Root required>
            <Field.Label>
              {text(t, 'musicOnHold.editModal.nameLabel', 'Name')}
            </Field.Label>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={text(
                t,
                'musicOnHold.editModal.namePlaceholder',
                'Enter playlist name',
              )}
              autoFocus
            />
          </Field.Root>
        </VStack>
      </form>
    </Modal>
  )
}
